import clsx from 'clsx';
import { useState, Activity, useEffect, useCallback } from 'react';

import { IconEye, IconSearch, IconAdjustments } from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import DateRangePicker from '@/components/DateRangePicker';
import ExportDropdown from '@/components/ExportDropdown';
import Input from '@/components/Input/Input';
import CustomTable from '@/components/Table/index';
import { LessonStageConfig, ContentStatusConfig } from '@/constants/constant';
import { EExportType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import { getContentList, exportContentList } from '@/service/contentService';
import { IContentGameData } from '@/types';
import {
  pickBy,
  downloadFile,
  customPredicate,
  formatLastActive,
} from '@/utils';

import GameDetailsModal from './components/GameDetailsModal';
import GamesFilterModal, {
  IGamesFilterValues,
} from './components/GamesFilterModal';

// ─── Types ───────────────────────────────────────────────────────────────────

type TGamesParams = {
  search?: string;
  offset?: number;
  limit?: number;
  order?: Record<string, string>;
  quarter?: string;
  category?: string;
  status?: string;
  start_date?: number;
  end_date?: number;
};

const PAGE_SIZE = 10;

const Games = () => {
  const { formatText } = useFormatText();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 500);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ASC' | 'DESC' | '';
  }>({ key: '', direction: '' });

  const [isOpenDetailsModal, setIsOpenDetailsModal] = useState(false);
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<IGamesFilterValues>({
    quarter: '',
    level: '',
    status: '',
  });

  const [games, setGames] = useState<IContentGameData[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);

  const levelOptions = Object.entries(LessonStageConfig)?.map(
    ([key, config]) => ({
      value: key,
      label: formatText(config?.label),
    })
  );

  const handleExport = async (type: 'csv' | 'pdf') => {
    setIsExportLoading(true);
    try {
      const query: Record<string, unknown> = {
        search: debouncedSearch || undefined,
        quarter: filterValues?.quarter || undefined,
        category: filterValues?.level || undefined,
        status: filterValues?.status || undefined,
        start_date: startDate
          ? Math.floor(new Date(startDate).getTime() / 1000)
          : undefined,
        end_date: endDate
          ? Math.floor(new Date(endDate).getTime() / 1000)
          : undefined,
        type: type === 'csv' ? EExportType.CSV : EExportType.PDF,
      };
      const res = await exportContentList(query);
      if (res) {
        const label = formatText('report-analytics.games.title')
          .replace(/\s+/g, '_')
          .toLowerCase();
        downloadFile(res, `${label}_${new Date().getTime()}.${type}`);
      }
    } catch (error) {
      console.error('Error exporting games:', error);
    } finally {
      setIsExportLoading(false);
    }
  };

  const fetchGames = useCallback(async () => {
    setIsLoading(true);

    let params: TGamesParams = {
      search: debouncedSearch || undefined,
      offset: (currentPage - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      order:
        sortConfig?.key && sortConfig?.direction
          ? { [sortConfig?.key]: sortConfig?.direction }
          : undefined,
      quarter: filterValues?.quarter || undefined,
      category: filterValues?.level || undefined,
      status: filterValues?.status || undefined,
      start_date: startDate
        ? Math.floor(new Date(startDate).getTime() / 1000)
        : undefined,
      end_date: endDate
        ? Math.floor(new Date(endDate).getTime() / 1000)
        : undefined,
    };

    params = pickBy(params, customPredicate) as TGamesParams;

    const response = await getContentList(params as Record<string, unknown>);
    if (response?.status) {
      setGames(response?.data || []);
      setTotal(response?.total || 0);
    }

    setIsLoading(false);
  }, [
    currentPage,
    debouncedSearch,
    sortConfig,
    filterValues,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterValues, startDate, endDate]);

  const handleFilterApply = (values: IGamesFilterValues) => {
    setFilterValues(values);
    setCurrentPage(1);
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev?.key === key
          ? prev?.direction === 'ASC'
            ? 'DESC'
            : prev?.direction === 'DESC'
              ? ''
              : 'ASC'
          : 'ASC',
    }));
  };

  const columns = [
    {
      title: 'report-analytics.games.table.games',
      key: 'game_name',
      width: '300px',
      sorter: true,
      handleSort: () => handleSort('game_name'),
      render: (row: IContentGameData) => (
        <div className="flex items-center gap-2">
          <AvatarGroupList
            list={[
              {
                id: row?.id,
                name: row?.title,
                image: row?.preview_image ?? '',
              },
            ]}
            size={40}
            avatarClassName="w-10 h-10 rounded-full"
          />
          <span className="text-sm font-medium text-neutral-primary whitespace-nowrap">
            {row?.title}
          </span>
        </div>
      ),
    },
    {
      title: 'report-analytics.games.table.quarter',
      key: 'quarters',
      width: '100px',
      sorter: true,
      handleSort: () => handleSort('quarters'),
      render: (row: IContentGameData) => (
        <span className="text-sm font-medium text-neutral-primary">
          {row?.quarters ?? '-'}
        </span>
      ),
    },
    {
      title: 'report-analytics.games.table.round',
      key: 'rounds',
      width: '100px',
      sorter: true,
      handleSort: () => handleSort('rounds'),
      render: (row: IContentGameData) => (
        <span className="text-sm font-medium text-neutral-primary">
          {row?.rounds ?? '-'}
        </span>
      ),
    },
    {
      title: 'report-analytics.games.table.level',
      key: 'stage',
      width: '150px',
      sorter: true,
      handleSort: () => handleSort('stage'),
      render: (row: IContentGameData) => {
        const config =
          LessonStageConfig[row?.category as keyof typeof LessonStageConfig];
        return config ? (
          <span
            className={clsx(
              'px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap',
              config?.className
            )}
          >
            {formatText(config?.label)}
          </span>
        ) : (
          '-'
        );
      },
    },
    {
      title: 'report-analytics.games.table.play-count',
      key: 'play_count',
      width: '150px',
      sorter: true,
      handleSort: () => handleSort('play_count'),
      render: (row: IContentGameData) => (
        <span className="text-xs font-medium text-neutral-primary bg-neutral-light-200 px-2 py-1 rounded-full">
          {row.play_count?.toLocaleString() ?? 0}
        </span>
      ),
    },
    {
      title: 'report-analytics.games.table.status',
      key: 'status',
      width: '150px',
      sorter: true,
      handleSort: () => handleSort('status'),
      render: (row: IContentGameData) => {
        const config =
          ContentStatusConfig[row?.status as keyof typeof ContentStatusConfig];
        return (
          <span
            className={clsx(
              'px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap capitalize',
              config?.className
            )}
          >
            {formatText(config?.label ?? '-')}
          </span>
        );
      },
    },
    {
      title: 'report-analytics.games.table.last-update',
      key: 'last_update',
      width: '150px',
      sorter: true,
      handleSort: () => handleSort('last_update'),
      render: (row: IContentGameData) => (
        <span className="text-sm font-medium text-neutral-secondary">
          {formatLastActive(row?.last_update ?? undefined, formatText)}
        </span>
      ),
    },
    {
      title: 'report-analytics.games.table.action',
      key: 'action',
      width: '100px',
      align: 'center' as const,
      render: (row: IContentGameData) => (
        <ActionDropdown
          actions={[
            {
              key: 'view',
              label: 'common.actions.view',
              icon: <IconEye size={18} />,
              onClick: () => {
                setSelectedGameId(row.id);
                setIsOpenDetailsModal(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  const isEmptySearch =
    games?.length === 0 &&
    !isLoading &&
    (debouncedSearch?.length > 0 ||
      !!filterValues.quarter ||
      !!filterValues.level ||
      !!filterValues.status ||
      !!startDate ||
      !!endDate);

  const isFilterApplied =
    !!filterValues?.quarter || !!filterValues?.level || !!filterValues?.status;

  return (
    <div className="flex flex-col gap-6">
      {/* Header Filters row */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <Input
            name="search"
            placeholder={formatText(
              'report-analytics.games.search-placeholder'
            )}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-500" />,
            }}
            className="min-w-80"
            inputClassName="py-[12px]"
          />
          <div className="relative">
            <Button
              icon={<IconAdjustments size={20} />}
              label={formatText('common.actions.filter')}
              variant="outline"
              className={clsx(
                'px-4 h-12 border-neutral-light-200',
                isFilterApplied && 'border-brand-default! text-brand-default!'
              )}
              onClick={() => setIsFilterModalOpen(true)}
            />
            {isFilterApplied && (
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
            )}
          </div>
        </div>

        <div className="flex justify-center items-center gap-2.5">
          <DateRangePicker
            placeholder={formatText(
              'report-analytics.learning.filter-date-range-placeholder'
            )}
            setFirstDate={setStartDate}
            setSecondDate={setEndDate}
            popupAlign="right"
            className="w-full! min-w-0! h-12! text-base! font-normal!"
          />
          <ExportDropdown
            onCsvExport={() => handleExport('csv')}
            onPdfExport={() => handleExport('pdf')}
            disabled={isExportLoading}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-bold text-neutral-primary">
          {formatText('report-analytics.games.title')}
        </h2>

        <CustomTable
          dataSource={games}
          columns={columns}
          isLoading={isLoading}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          total={total}
          fieldName={sortConfig.key}
          sortDirection={sortConfig.direction}
          rootClassName="lg:h-[calc(100vh-320px)]"
          isEmptySearch={isEmptySearch}
          onRowClick={(id) => {
            setSelectedGameId(id);
            setIsOpenDetailsModal(true);
          }}
        />
      </div>

      <Activity mode={isOpenDetailsModal ? 'visible' : 'hidden'}>
        <GameDetailsModal
          setIsOpen={setIsOpenDetailsModal}
          gameId={selectedGameId}
        />
      </Activity>
      <Activity mode={isFilterModalOpen ? 'visible' : 'hidden'}>
        <GamesFilterModal
          setIsOpen={setIsFilterModalOpen}
          onApply={handleFilterApply}
          initialValues={filterValues}
          levelOptions={levelOptions}
        />
      </Activity>
    </div>
  );
};

export default Games;
